// Copyright 2016 Sorama B.V.

/*
 * Adapted from: https://gist.run/?id=d9d8dd9df7be2dd2f59077bad3bfb399
 * as mentioned in: https://github.com/aurelia/binding/issues/443#issuecomment-232007548
 */
import { bindable, bindingMode, customAttribute, DOM, inject } from "aurelia-framework";

export type UpdateTriggers = "input" | "blur";

@customAttribute("number-value", bindingMode.twoWay)
@inject(DOM.Element)
export class NumberValue {
    private input: HTMLInputElement;

    // The value of the number input
    @bindable value: number;

    // The amount of decimals to round the input number to.
    @bindable decimals: number = undefined;

    // When to trigger the binding when the value changes. Supported values are 'input' and 'blur'
    @bindable updateTrigger: UpdateTriggers = "input";

    constructor(input: HTMLInputElement) {
        this.input = input;
    }

    private valueChanged(): void {
        if (typeof this.value === "string") {
            this.value = parseFloat((<string>this.value).replace(",", "."));
        }

        // synchronize the input element with the bound value
        if (this.value === undefined || this.value === null || isNaN(this.value)) {
            this.input.value = "";
        } else {
            // Parse existing number
            const existingInput = this.getNumber(this.input.value);

            if (!isNaN(existingInput) && existingInput === this.value) return; // Number matches, no need to update for user convenience

            this.input.value =
                this.decimals === undefined
                    ? this.value.toString()
                    : typeof this.decimals === "string" && <string>this.decimals === "scientific"
                      ? this.value.toExponential(6)
                      : this.value.toFixed(this.decimals);
            // This is dirty hacking
            if (this.input.onchange) {
                this.input.onchange(null);
            }
        }
    }

    public updateTriggerChanged(newValue: string, oldValue: string) {
        if (oldValue) this.input.removeEventListener(oldValue, this.inputEvent);

        if (newValue) this.input.addEventListener(newValue, this.inputEvent);
    }

    private inputEvent = (): void => {
        // blank input maps to null value
        if (this.input.value === "") {
            this.value = null;
            return;
        }

        // do we have a number?
        const num = this.getNumber(this.input.value);
        if (!isNaN(num)) {
            // Check that the value does not end on a '.' or ',' to prevent removing the '.' while typing
            if (this.input.value.endsWith(".") || this.input.value.endsWith(",")) return;

            let value = num;

            // Make sure value lies between the min and max value
            if (this.input.min) {
                const min: number = parseFloat(this.input.min);
                if (!Number.isNaN(min)) {
                    value = Math.max(min, value);
                }
            }

            if (this.input.max) {
                const max: number = parseFloat(this.input.max);
                if (!Number.isNaN(max)) {
                    value = Math.min(max, value);
                }
            }

            // yes! update the binding.
            const roundingFactor = Math.pow(10, this.decimals);
            this.value =
                this.decimals === undefined || isNaN(roundingFactor)
                    ? value
                    : Math.round(value * roundingFactor) / roundingFactor;

            if (this.value !== num) {
                // We had to correct the value, reset the value to match the actual value
                this.valueChanged();
            }
        } else {
            this.valueChanged();
        }
    };

    private keyEvent = (ev: KeyboardEvent) => {
        if (ev.key === "Enter") {
            this.inputEvent();
        }
    };

    public bind() {
        this.valueChanged();
        this.input.addEventListener(this.updateTrigger, this.inputEvent);
        this.input.addEventListener("keyup", this.keyEvent);
    }

    public unbind() {
        this.input.removeEventListener("keyup", this.keyEvent);
        this.input.removeEventListener(this.updateTrigger, this.inputEvent);
    }

    private getNumber(value: string): number {
        if (value == undefined || value == null || value == "") return NaN;

        const num = Number(value.replace(",", "."));
        return !Number.isNaN(num) && Number.isFinite(num) ? num : NaN;
    }
}
